import { supabase } from "@/lib/supabase/browser-client"
import { Tables } from "@/supabase/types"
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js"
import { Dispatch, SetStateAction } from "react"
import { printObject } from '@/lib/utils'
import { HandlerConfig } from "../base-subscription-manager"
import { createClientLogger } from "@/utils/logger/clientLogger"
import Authuser from "@/types/base/authuser"
import { isAidonAdmin } from "@/db/tenants"
import { v4 as uuidv4 } from "uuid"
import { getUserWorkspacesByTenantId } from "@/db/workspaces"

const logger = createClientLogger('realtime.workspaces')

type Row = Tables<'workspaces'>

const appliesToUser = async (workspaceId:string, authUser: Authuser | null, tenantId: string | null): Promise<boolean> => {
	if (!authUser || !tenantId) return false
	const aidonAdmin = await isAidonAdmin(supabase, authUser)
	const workspaces = await getUserWorkspacesByTenantId(supabase, tenantId, aidonAdmin)
	return workspaces.some(ws => ws.id === workspaceId)
}

/**
 * This method doesn't handle insert since at that time the workspaces are not known.
 * It also doesn't handle delete since workspace relationship join table is better suited since it covers all cases
 * @param workspaceId
 * @param setWorkspaces
 * @returns
 */
export function makeWorkspaceHandler(
	authUserRef: React.RefObject<Authuser | null>,
	tenantIdRef: React.RefObject<string | null>,
	setWorkspaces: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<Row> {
	const uuid = uuidv4()
	return {
		key: `workspaces-${uuid}`,
		channelName: `workspaces-${uuid}`,
		events: ['INSERT', 'UPDATE', 'DELETE'],
		schema: 'public',
		table: 'workspaces',
		handler: async (payload: RealtimePostgresChangesPayload<Row>) => {
			if (payload.eventType == "INSERT") {
				const workspace = payload.new as Row

				if (await appliesToUser(workspace.id, authUserRef.current, tenantIdRef.current)) {
					setWorkspaces(prev => {
						if (prev.some(p => p.id === workspace.id)) return prev
						logger.info(`Handling workspace Insert:${printObject(workspace)}`)
						return [...prev, workspace]
					})
				}
			}
			else if (payload.eventType === 'UPDATE') {  //don't need to check applicability
				const provider = payload.new as Row
				setWorkspaces(prev => {
					const exists = prev.some(p => p.id === provider.id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling workspace update:${printObject(provider)}`)
					return prev.map(p => p.id === provider.id ? provider : p)
				})
			}
			else if (payload.eventType == "DELETE") {  //don't need to check applicability
				const provider = payload.old as Row
				setWorkspaces(prev => {
					const exists = prev.some(p => p.id === provider.id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling workspace Delete:${provider.id}`)
					return prev.filter(p => p.id !== provider.id)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}